import React, { useMemo } from 'react';
import {
    Stack,
    Image,
    Text,
    IStackStyles,
    IImageStyles,
    ITextStyles,
    TooltipHost,
    Icon,
} from '@fluentui/react';
import { ShareLinkButton } from './fileShareLinkButton';
import { ServerFile } from '../../../model';
// eslint-disable-next-line max-len
import { FileDropdownButton } from './fileDropdownButton';
import { shallowEqual } from 'react-redux';
import { getImageByContetntType, resolveThemeColors } from '../../../helpers';
import { FolderIcon } from '../../common/folderIcon';
import { selectTeamMember } from '../../../store/selectors/conversation';
import { useAppSelector } from '../../../store';
import { ChatMemberRole } from '../../../services/microsoft/msGraphApi';
import * as path from 'path';

import {
    selectDefaultFolderId,
    selectDefaultFolderScope,
} from '../../../store/selectors/defaultFolder';

type Props = {
    file: ServerFile;
};

type Styles = {
    container: IStackStyles;
    image: Partial<IImageStyles>;
    name: ITextStyles;
    extension: ITextStyles;
};

const styles: Styles = {
    container: {
        root: {
            minWidth: 0,
        },
    },
    image: {
        root: {
            flexShrink: 0,
        },
    },
    name: {
        root: {
            overflow: 'hidden',
        },
    },
    extension: {
        root: {
            color: 'grey',
            marginLeft: '0 !important',
        },
    },
};

export const FileDetailsContent = ({ file }: Props): JSX.Element => {
    const src = getImageByContetntType(
        file.contenttype ?? '',
        file.type,
        file.name,
    );

    const parsedPath = useMemo(() => path.parse(file.name), [file.name]);
    const colors = useAppSelector(resolveThemeColors, shallowEqual);
    const teamMember = useAppSelector(selectTeamMember);
    const isOwner = teamMember?.roles?.includes(ChatMemberRole.Owner);
    const defaultFolderId = useAppSelector(selectDefaultFolderId);
    const defaultScope = useAppSelector(selectDefaultFolderScope);
    const isDefault =
        Boolean(defaultFolderId) && file.fileId === Number(defaultFolderId);

    return (
        <Stack
            horizontal={true}
            tokens={{ childrenGap: '1rem' }}
            horizontalAlign="space-between"
            verticalAlign="center"
        >
            <Stack
                horizontal={true}
                tokens={{ childrenGap: '0.8rem' }}
                horizontalAlign="start"
                verticalAlign="center"
                styles={styles.container}
            >
                {file.type === 'directory' ? (
                    <FolderIcon
                        shared={file.shared}
                        size={32}
                        color={colors.icon}
                    />
                ) : (
                    <Image
                        src={src}
                        height={32}
                        width={32}
                        styles={styles.image}
                    />
                )}
                {isOwner && isDefault && (
                    <TooltipHost
                        content={
                            defaultScope === 'team'
                                ? 'Team default folder'
                                : 'Channel default folder'
                        }
                    >
                        <Icon
                            iconName={
                                defaultScope === 'team'
                                    ? 'FavoriteStarFill'
                                    : 'FavoriteStar'
                            }
                            styles={{
                                root: {
                                    color: colors.icon,
                                    marginRight: '0.3rem',
                                    cursor: 'pointer',
                                },
                            }}
                        />
                    </TooltipHost>
                )}
                <Text styles={styles.name}>{parsedPath.name}</Text>
                <Text styles={styles.extension}>{parsedPath.ext}</Text>
            </Stack>
            <Stack
                horizontal={true}
                horizontalAlign="start"
                verticalAlign="center"
            >
                <ShareLinkButton fileId={file.fileId} />
                <FileDropdownButton
                    filePath={file.path}
                    type={file.type}
                    fileId={file.fileId}
                />
            </Stack>
        </Stack>
    );
};
